import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded'
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded'

const WHEN_TASK = [
  ['You don’t have the document yet', 'Schedule and assign getting it. A deadline and reminders, instead of leaving an open gap.'],
  ['It recurs', 'Monthly test, annual certificate. The task auto-reminds every renewal. An upload is a one-off and won’t chase the next one.'],
  ['It’s proven by doing', 'An inspection or test recorded on a form with a sign-off. Not a document you already hold.'],
  ['You want a clear owner and audit trail', 'Who did it, when, and the result. Visible and tracked.'],
  ['It’s a task-type obligation', 'These can only be met by completing a task; uploading a file won’t satisfy them.'],
]

const TaskVsEvidenceExplainer = ({ label = 'Task or upload?' }) => {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!open) return
    const onKey = e => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [open])

  return (
    <Wrap>
      <InfoButton
        type='button'
        aria-label='When should I upload evidence versus create a task?'
        aria-expanded={open}
        onClick={e => { e.stopPropagation(); setOpen(true) }}
      >
        <InfoOutlinedIcon style={{ fontSize: 15 }} />
        <span>{label}</span>
      </InfoButton>

      {open && createPortal(
        <Backdrop onMouseDown={e => { if (e.target === e.currentTarget) setOpen(false) }}>
          <Dialog role='dialog' aria-modal='true' aria-label='Upload evidence or create a task'>
            <Header>
              <HeaderTitle>Upload evidence or create a task?</HeaderTitle>
              <CloseBtn type='button' aria-label='Close' onClick={() => setOpen(false)}>
                <CloseRoundedIcon style={{ fontSize: 18 }} />
              </CloseBtn>
            </Header>

            <Body>
              <Lead>
                Both satisfy the obligation. The choice is simple. <strong>Upload</strong> when you already
                have the proof. <strong>Create a task</strong> when the work still needs doing, or needs to
                keep happening on a schedule.
              </Lead>

              <Cards>
                <OptCard $c='#15803d'>
                  <OptHead $c='#15803d'><UploadFileRoundedIcon style={{ fontSize: 17 }} /> Upload evidence</OptHead>
                  <OptText>You <strong>already have the document</strong>. A certificate, report or EPC. Upload it and the obligation is met straight away.</OptText>
                  <OptWhen>Best when the proof exists and you’re holding it.</OptWhen>
                </OptCard>
                <OptCard $c='#33becc'>
                  <OptHead $c='#33becc'><AssignmentTurnedInRoundedIcon style={{ fontSize: 17 }} /> Create a task</OptHead>
                  <OptText>The work <strong>still needs doing</strong>, or it <strong>repeats</strong>. A task assigns someone, sets a deadline, and reminds you each cycle. And completing it becomes the evidence.</OptText>
                  <OptWhen>Best when you don’t have it yet, it recurs, or it’s proven by doing.</OptWhen>
                </OptCard>
              </Cards>

              <WhyHead>Create a task when…</WhyHead>
              <List>
                {WHEN_TASK.map(([h, t]) => (
                  <RowItem key={h}>
                    <Dot $c='#33becc' />
                    <Text><strong>{h}.</strong> {t}</Text>
                  </RowItem>
                ))}
              </List>

              <Rule>
                <strong>Rule of thumb:</strong> have the document already → <em>Upload evidence</em>. Need the
                work done, or kept up on a schedule → <em>Create a task</em>.
              </Rule>
            </Body>
          </Dialog>
        </Backdrop>,
        document.body
      )}
    </Wrap>
  )
}

export default TaskVsEvidenceExplainer

const Wrap = styled.span` display: inline-flex; `

const InfoButton = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 5px;
  background: rgba(255, 255, 255, 0.10);
  border: 1px solid rgba(12, 18, 41, 0.12);
  color: inherit;
  border-radius: 999px;
  padding: 4px 11px 4px 8px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 160ms ease;
  &:hover { background: rgba(12, 18, 41, 0.06); }
`

const Backdrop = styled.div`
  position: fixed; inset: 0; z-index: 100070;
  background: rgba(12, 18, 41, 0.45);
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
`

const Dialog = styled.div`
  background: #fff; border-radius: 16px;
  width: 660px; max-width: 100%; max-height: 88vh;
  display: flex; flex-direction: column; overflow: hidden;
  box-shadow: 0 24px 60px rgba(12, 18, 41, 0.28);
`

const Header = styled.div`
  flex-shrink: 0;
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 16px 20px; border-bottom: 1px solid #f0f0f2;
`

const HeaderTitle = styled.h4`
  margin: 0; font-size: 16px; font-weight: 800;
  color: ${({ theme }) => theme.colors.haiti};
`

const CloseBtn = styled.button`
  font: inherit; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px;
  border: none; background: transparent;
  color: ${({ theme }) => theme.colors.grey[800]};
  border-radius: 8px; cursor: pointer; opacity: 0.7;
  &:hover { opacity: 1; background: rgba(12, 18, 41, 0.05); }
`

const Body = styled.div` overflow-y: auto; overflow-x: hidden; padding: 18px 20px 22px; `

const Lead = styled.p`
  margin: 0 0 16px; font-size: 13px; line-height: 1.55; color: #4d4d56;
  strong { color: ${({ theme }) => theme.colors.haiti}; }
`

const Cards = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px;
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`

const OptCard = styled.div`
  border: 1px solid ${({ $c }) => `${$c}33`};
  background: ${({ $c }) => `${$c}0D`};
  border-radius: 12px; padding: 12px 13px;
  display: flex; flex-direction: column; gap: 6px;
`

const OptHead = styled.div`
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 13.5px; font-weight: 800; color: ${({ $c }) => $c};
`

const OptText = styled.p`
  margin: 0; font-size: 12.5px; line-height: 1.5; color: #4d4d56;
  strong { color: ${({ theme }) => theme.colors.haiti}; }
`

const OptWhen = styled.span` font-size: 11.5px; font-weight: 700; color: #6e6e78; margin-top: 2px; `

const WhyHead = styled.div`
  margin: 18px 0 10px;
  font-size: 11px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; color: #8a909b;
`

const List = styled.div` display: flex; flex-direction: column; gap: 10px; `

const RowItem = styled.div` display: flex; gap: 9px; align-items: flex-start; `

const Dot = styled.span`
  flex: 0 0 auto; width: 9px; height: 9px; margin-top: 5px; border-radius: 50%;
  background: ${({ $c }) => $c}; box-shadow: 0 0 0 2px ${({ $c }) => `${$c}33`};
`

const Text = styled.span`
  font-size: 12.5px; line-height: 1.5; color: #4d4d56;
  strong { color: ${({ theme }) => theme.colors.haiti}; }
`

const Rule = styled.p`
  margin: 18px 0 0; padding: 11px 13px; border-radius: 10px;
  background: #f6fbfa; border: 1px solid #e6f3f1;
  font-size: 12.5px; line-height: 1.5; color: #2f4f49;
  strong { color: ${({ theme }) => theme.colors.haiti}; }
  em { font-style: normal; font-weight: 800; }
`
